<template>
  <LetterLayout :model="model" v-on="$attrs">
    <template #from>
      <div class="d-flex flex-row align-items-start gap-4">
        <div class="flex-grow-1 small">
          <div>CITY OF SOUTH PORTLAND</div>
          <div>P.O. BOX 9422 25 COTTAGE ROAD</div>
          <div>SOUTH PORTLAND, ME 04116</div>
        </div>
        <img width="200" :src="model.city.townSealUrl" />
        <div class="border-start border-dark border-2 ps-4 d-flex flex-column gap-2">
          <div>
            <div class="fw-semibold">Jessica A. Hughes</div>
            <div class="fst-italic">City Clerk</div>
          </div>
          <div>
            <div class="fw-semibold">Dana Chabot</div>
            <div class="fst-italic">Licensing Administrator</div>
          </div>
        </div>
      </div>
    </template>

    <template #to>
      <div class="d-flex flex-column small gap-2">
        <div>
          <div>********************************</div>
          <div v-for="owner in model.ownersFormatted" :key="owner">
            {{ owner }}
          </div>
          <div v-for="addressLine in model.ownerAddressLines" :key="addressLine">
            {{ addressLine }}
          </div>
          <div>********************************</div>
        </div>
        <div class="py-0"/>
      </div>
    </template>

    <template #date>
      <div>
        {{ model.dateFormatted }}
      </div>
    </template>

    <template #subject>
      <div class="fw-semibold">
        RE: Short-Term Rental Registration Required for {{ model.addressFormatted }}, South Portland, ME 04106
      </div>
    </template>

    <template #signature-image>
      <img :src="signature" height="56" style="margin-bottom: -4px" />
    </template>

    <template #footer>
      Office of the City Clerk, 25 Cottage Road, P.O. Box 9422, South Portland, ME 04116-9422
      <br/>
      <span class="text-primary fw-semibold">southportland.gov</span> | <span class="fw-semibold">Phone:</span> (207) 767-3201 | <span class="fw-semibold">Fax:</span> (207) 767-7620
    </template>

    <p>
      Dear {{ model.ownerFormatted }},
    </p>
    <template v-if="first">
      <p>
        It has come to our attention that your property, located at <b>{{ model.addressFormatted }}</b>, is being advertised as available for short-term rental without being registered with the City of South Portland as required by Sec. 14-804 of the City's Code of Ordinances (Chapter 14, Licensing). Last year, the City adopted new rules regarding Short-Term Residential Rentals (defined as a rental of a dwelling unit or portion thereof for less than 30 consecutive days) that prohibits Non-Hosted Home Stays in all Residential Zoning Districts and requires all eligible dwelling units operating on a short-term rental basis to be registered with the City. Those rules became effective on January 1, 2019.
      </p>
      <p>
        If your property is being used for short-term rentals, and it is eligible to be registered, then we respectfully request that you register the dwelling unit with the City at your earliest convenience by doing the following:
      </p>
      <ol>
        <li>
          Confirm eligibility to register a Hosted or Non-Hosted Home Stay Short-Term Residential Rental by contacting the Code Enforcement Office at (207) 767-7603.
        </li>
        <li>
          If the dwelling unit is eligible, please register your property online here: <a href="https://www.southportland.gov/749/Registration" target="_blank">https://www.southportland.gov/749/Registration</a>.
        </li>
      </ol>
      <p>
        If your dwelling unit is ineligible to be registered for the type of short-term rental you are currently providing, please cease advertising and operating your property on a short-term basis immediately.
      </p>
      <p>
        Compliance with regulations governing short-term residential rentals is ultimately the responsibility of the property owner. However, the City understands that some time may be necessary to achieve compliance. Therefore, the City is setting 30 days from the date of this letter as the deadline for registration. If your property is not in compliance after that 30-day time period has expired, you may be subject to additional enforcement action that could lead to financial penalties. This City hopes to avoid future enforcement action and is committed to assisting property owners with making this transition.
      </p>
      <p>
        If you believe you are receiving this letter in error, or if you have any questions, please contact us at tips@southportland.org or call (207) 767-7628. More information regarding the duties and obligations of property owners operating a short-term rental property can be found at  www.southportland.gov.
      </p>
      <p>
        The Greater Portland Council of Governments is contracted to help the City of South Portland with short term
        rental compliance. If you believe you are receiving this letter in error, or if you have any questions, please
        contact Erica Diesl at ediesl@gpcog.org. More information regarding the duties and obligations of property
        owners operating a short-term rental property can be found at www.southportland.gov.
      </p>
    </template>
    <template v-else>
      <p>
        You are hereby notified as the owner of the above-referenced property that you are in violation of the City of South Portland Code of Ordinances, Chapter 14, regarding Short-Term Residential Rentals. This is a follow-up to the City's prior letter to you dated {{ model.dateOfLastLetterFormatted }}.
      </p>
      <p>
        Pursuant to Sec. 14-804 of the City's Code of Ordinances, it is unlawful to advertise and operate property on a short-term rental basis (defined as any person who rents, licenses, occupies or has the right to occupy a dwelling unit or accessory dwelling unit, in whole or in part, for less than 30 consecutive days) in South Portland without first having registered the unit with the City Clerk's office. The City of South Portland has evidence that your property is being advertised and/or rented on a short-term basis without having registered the dwelling unit with the City Clerk's office.
      </p>
      <p>
        Accordingly, effective immediately, you must discontinue advertising and/or renting your property on a short-term basis, cancel any existing reservations, and remove all public advertisements pertaining to the short-term rental of the property.
      </p>
      <p>
        As a final reminder, under Sec. 14-805 of the City's Code of Ordinances, Non-Hosted Short-Term Rentals are prohibited from operating in a residential zone. If your property is eligible to be registered with the City (Hosted or Non-Hosted), you must register your dwelling unit at the City Clerk's office prior to advertising and/or renting the property on a short-term basis.
      </p>
      <p>
        Please be advised that the penalty for violating Sec. 14-804 of the City's Code of Ordinances is a fine of $1,000 per day for the first offense and a fine of $1,500 per day for each additional offense, to be recovered upon complaint by the City to Maine District or Superior Court in Portland. In the event you fail to cease advertising and/or renting on a short-term basis as required by the Notice of Violation, the City will be forced to initiate legal action to achieve compliance with its Code of Ordinances.
      </p>
      <p>
        If you have any questions, please contact the City Clerk's office at (207) 767-7628 or via email at tips@southportland.org
      </p>
    </template>
  </LetterLayout>
</template>

<script>
import LetterLayout from "@/components/letters/LetterLayout.vue";
import signature from './sopo-signature.png';
import {LetterContext} from "@/models/letterContext";

export default {
  components: {LetterLayout},
  props: {
    model: LetterContext,
    first: Boolean,
    showBarcode: Boolean
  },
  data() {
    return {
      signature
    };
  }
}
</script>
